'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Settings2, Plus, Download, X, RefreshCw, ExternalLink } from 'lucide-react';
import { METRICS, DEFAULT_ACTIVE, MAX_ACTIVE, type MetricId, type MetricDefinition } from './lib/metric-definitions';
import { useMetricsData, type DateRange, type MetricData } from './hooks/use-metrics-data';
import { MetricTile } from './components/metric-tile';
import { MultiLineChart } from './components/multi-line-chart';
import { MetricPicker } from './components/metric-picker';
import { MonitorBackView } from './components/monitor-back-view';
import { isoDate, startOfDay, addDays, fmtMoney, fmtCount } from './lib/format';

type RangePreset = '1' | '7' | '30' | '90' | 'custom';
type CompareMode = 'none' | 'previous' | 'yoy' | 'custom';

const STORAGE_KEY = 'sf-monitor-widget-prefs';

interface Prefs {
  metrics: MetricId[];
  rangePreset: RangePreset;
  customFrom?: string;
  customTo?: string;
  compareMode: CompareMode;
  compareFrom?: string;
  compareTo?: string;
  isFlipped?: boolean;
}

function defaultPrefs(): Prefs {
  return {
    metrics: [...DEFAULT_ACTIVE],
    rangePreset: '30',
    compareMode: 'none',
    isFlipped: false,
  };
}

function loadPrefs(): Prefs {
  if (typeof window === 'undefined') return defaultPrefs();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPrefs();
    const parsed = JSON.parse(raw);
    return {
      metrics: Array.isArray(parsed.metrics) && parsed.metrics.length
        ? parsed.metrics.filter((m: any) => !!METRICS[m as MetricId])
        : DEFAULT_ACTIVE,
      rangePreset: ['1', '7', '30', '90', 'custom'].includes(parsed.rangePreset) ? parsed.rangePreset : '30',
      customFrom: parsed.customFrom,
      customTo: parsed.customTo,
      compareMode: ['none', 'previous', 'yoy', 'custom'].includes(parsed.compareMode) ? parsed.compareMode : 'none',
      compareFrom: parsed.compareFrom,
      compareTo: parsed.compareTo,
      isFlipped: Boolean(parsed.isFlipped),
    };
  } catch {
    return defaultPrefs();
  }
}

function computePrimaryRange(prefs: Prefs): DateRange {
  const today = startOfDay(new Date());
  if (prefs.rangePreset === 'custom' && prefs.customFrom && prefs.customTo) {
    return {
      from: new Date(prefs.customFrom),
      to: new Date(prefs.customTo),
    };
  }
  const days = prefs.rangePreset === 'custom' ? 30 : parseInt(prefs.rangePreset, 10);
  return { from: addDays(today, -(days - 1)), to: today };
}

function computeCompareRange(prefs: Prefs, primary: DateRange): DateRange | null {
  if (prefs.compareMode === 'none') return null;
  const dayMs = 86400000;
  const primaryLen = Math.round((primary.to.getTime() - primary.from.getTime()) / dayMs) + 1;
  if (prefs.compareMode === 'previous') {
    return {
      from: addDays(primary.from, -primaryLen),
      to: addDays(primary.from, -1),
    };
  }
  if (prefs.compareMode === 'yoy') {
    return {
      from: new Date(primary.from.getFullYear() - 1, primary.from.getMonth(), primary.from.getDate()),
      to: new Date(primary.to.getFullYear() - 1, primary.to.getMonth(), primary.to.getDate()),
    };
  }
  if (prefs.compareMode === 'custom' && prefs.compareFrom && prefs.compareTo) {
    return { from: new Date(prefs.compareFrom), to: new Date(prefs.compareTo) };
  }
  return null;
}

export function MonitorPerformanceWidget() {
  const [prefs, setPrefs] = useState<Prefs>(() => loadPrefs());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rangeModalOpen, setRangeModalOpen] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [detailMetric, setDetailMetric] = useState<MetricId | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {}
  }, [prefs]);

  const primaryRange = computePrimaryRange(prefs);
  const compareRange = computeCompareRange(prefs, primaryRange);

  const toggleMetric = (id: MetricId) => {
    setPrefs((p) => {
      if (p.metrics.includes(id)) {
        if (p.metrics.length === 1) return p;
        return { ...p, metrics: p.metrics.filter((m) => m !== id) };
      } else {
        const next = [...p.metrics];
        if (next.length >= MAX_ACTIVE) next.shift();
        next.push(id);
        return { ...p, metrics: next };
      }
    });
  };

  const setRangePreset = (r: RangePreset) => {
    if (r === 'custom') {
      setRangeModalOpen(true);
      return;
    }
    setPrefs((p) => ({ ...p, rangePreset: r }));
  };

  const applyCustomRange = (from: string, to: string) => {
    setPrefs((p) => ({ ...p, rangePreset: 'custom', customFrom: from, customTo: to }));
    setRangeModalOpen(false);
  };

  const setCompareMode = (mode: CompareMode) => {
    if (mode === 'custom') {
      setCompareModalOpen(true);
      return;
    }
    setPrefs((p) => ({ ...p, compareMode: mode }));
  };

  const toggleFlip = () => setPrefs((p) => ({ ...p, isFlipped: !p.isFlipped }));

  const applyCustomCompare = (from: string, to: string) => {
    setPrefs((p) => ({ ...p, compareMode: 'custom', compareFrom: from, compareTo: to }));
    setCompareModalOpen(false);
  };

  const { metrics, isLoading } = useMetricsData({
    activeMetrics: prefs.metrics,
    primary: primaryRange,
    compare: compareRange,
  });

  const recommended: MetricId[] = [];
  const visibleTiles: MetricId[] = [...prefs.metrics];
  for (const r of recommended) {
    if (!visibleTiles.includes(r) && visibleTiles.length < 5) visibleTiles.push(r);
  }

  const rangeLabel = (() => {
    if (prefs.rangePreset === 'custom') {
      const f = new Date(prefs.customFrom || '').toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' });
      const t = new Date(prefs.customTo || '').toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' });
      return f + ' – ' + t;
    }
    const days = parseInt(prefs.rangePreset, 10);
    return days === 1 ? '24 ore' : days + ' zile';
  })();

  const primaryDays = Math.round((primaryRange.to.getTime() - primaryRange.from.getTime()) / 86400000) + 1;

  const compareLabel: string | null = (() => {
    if (prefs.compareMode === 'none' || !compareRange) return null;
    if (prefs.compareMode === 'previous') return 'perioada imediat anterioară';
    if (prefs.compareMode === 'yoy') return 'aceeași perioadă, anul trecut';
    return compareRange.from.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' }) + ' – ' + compareRange.to.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' });
  })();

  return (
    <div className="col-span-12">
      <article className="relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-[18px] overflow-hidden shadow-[0_1px_0_rgba(15,14,15,0.015),0_4px_16px_rgba(15,14,15,0.03)]" style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
        {/* Decorative crosshatch corner */}
        <div
          aria-hidden
          className="absolute top-3 right-3 w-8 h-8 opacity-40 pointer-events-none rounded"
          style={{
            backgroundImage:
              'linear-gradient(45deg, transparent 45%, #E8E4D8 46%, #E8E4D8 54%, transparent 55%),' +
              'linear-gradient(-45deg, transparent 45%, #E8E4D8 46%, #E8E4D8 54%, transparent 55%)',
            backgroundSize: '6px 6px',
          }}
        />

        {/* HEADER */}
        <div className="flex items-center justify-between gap-5 px-6 pt-5 pb-4 border-b border-neutral-100 dark:border-neutral-800 flex-wrap relative z-[2]">
          <div className="flex items-baseline gap-3">
            <h2
              className="m-0 font-semibold tracking-[-0.01em] leading-none text-neutral-900 dark:text-neutral-100"
              style={{ fontSize: '1.5rem' }}
            >
              Monitor{' '}
              <em style={{ fontStyle: 'italic', color: 'rgb(var(--primary-500))', fontWeight: 500 }}>
                performanță
              </em>
            </h2>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="inline-flex p-[3px] bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-[9px]">
              {(['1', '7', '30', '90', 'custom'] as RangePreset[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRangePreset(r)}
                  className={
                    'text-[0.7188rem] font-medium tracking-wider px-3 py-1.5 border-0 rounded-md cursor-pointer transition-all ' +
                    (prefs.rangePreset === r
                      ? 'bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                      : 'bg-transparent text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100')
                  }
                >
                  {r === '1' ? 'Azi' : r === 'custom' ? 'Custom' : r + 'z'}
                </button>
              ))}
            </div>
            <button
              onClick={toggleFlip}
              className={'w-8 h-8 border rounded-lg cursor-pointer grid place-items-center transition ' + (prefs.isFlipped ? 'bg-primary text-white border-primary hover:bg-primary-700' : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 hover:border-neutral-300 dark:hover:border-neutral-600')}
              title={prefs.isFlipped ? 'Revino la chart linii' : 'Vizualizare alternativă (coloane + text)'}
            >
              <RefreshCw className="h-3.5 w-3.5" style={{ transform: prefs.isFlipped ? 'scaleX(-1)' : 'scaleX(1)' }} />
            </button>
            <button
              onClick={() => setPickerOpen(true)}
              className="w-8 h-8 border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 rounded-lg cursor-pointer grid place-items-center text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 hover:border-neutral-300 dark:hover:border-neutral-600 transition"
              title="Setări / adaugă metrică"
            >
              <Settings2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* 3D FLIP — real page-turn animation, translateZ hack keeps SVG sharp */}
        <div style={{ perspective: '2400px' }}>
          <div
            style={{
              position: 'relative',
              transformStyle: 'preserve-3d',
              transition: 'transform 0.72s cubic-bezier(0.25, 0.85, 0.35, 1.05)',
              transform: prefs.isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
          <div
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'translate3d(0, 0, 0.01px)',
              WebkitTransform: 'translate3d(0, 0, 0.01px)',
              visibility: prefs.isFlipped ? 'hidden' : 'visible',
              transitionProperty: 'visibility',
              transitionDelay: prefs.isFlipped ? '0.36s' : '0s',
            }}
          >
            <div key="front">
        {/* TILES */}
        <div
          className="grid border-b border-neutral-100 dark:border-neutral-800"
          style={{
            gridTemplateColumns: `repeat(${visibleTiles.length}, minmax(150px, 1fr)) 48px`,
            background: 'linear-gradient(to right, #FFFDF8, #FFFFFF)',
          }}
        >
          {visibleTiles.map((id) => (
            <MetricTile
              key={id}
              def={METRICS[id]}
              data={metrics[id]}
              isActive={prefs.metrics.includes(id)}
              onShowDetail={() => setDetailMetric(id)}
              onRemove={() => toggleMetric(id)}
              canRemove={prefs.metrics.length > 1}
              isLoading={isLoading && !metrics[id]}
            />
          ))}
          <button
            onClick={() => setPickerOpen(true)}
            className="grid place-items-center bg-transparent border-0 cursor-pointer text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-primary transition border-l border-neutral-100 dark:border-neutral-800"
            title="Adaugă metrică"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>

        {/* CHART */}
        <div className="relative px-6 pt-6 pb-5 bg-white dark:bg-neutral-900">
          <div className="mb-3.5 flex justify-between items-start gap-4">
            <div className="text-[0.6875rem] text-neutral-500 tracking-[0.02em]">
              <b className="text-neutral-900 dark:text-neutral-100 font-semibold">{prefs.metrics.length}</b>{' '}
              {prefs.metrics.length === 1 ? 'metrică' : 'metrici'} · perioadă{' '}
              <b className="text-neutral-900 dark:text-neutral-100 font-semibold">{rangeLabel}</b>
              {prefs.compareMode !== 'none' && compareRange && (
                <>
                  {' '}· comparat cu <b className="text-neutral-900 dark:text-neutral-100">
                    {prefs.compareMode === 'previous' && 'perioada anterioară'}
                    {prefs.compareMode === 'yoy' && 'aceeași perioadă anul trecut'}
                    {prefs.compareMode === 'custom' && (
                      compareRange.from.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' }) +
                      ' – ' +
                      compareRange.to.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' })
                    )}
                  </b>
                </>
              )}
            </div>
          </div>
          <MultiLineChart
            metrics={metrics}
            activeMetrics={prefs.metrics}
            comparePrevious={prefs.compareMode !== 'none'}
            rangeDays={primaryDays}
          />
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between gap-4 px-6 py-3.5 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/50 flex-wrap">
          <div className="flex items-center gap-3.5">
            <button
              onClick={() => alert('Export CSV — TODO')}
              className="text-[0.75rem] font-medium text-neutral-700 dark:text-neutral-300 bg-transparent border-0 cursor-pointer py-1 inline-flex items-center gap-1.5 tracking-[0.01em] border-b border-dotted border-neutral-300 dark:border-neutral-600 hover:text-primary hover:border-primary transition"
            >
              <Download className="h-3 w-3" />
              Exportă date (CSV)
            </button>
            <span className="text-neutral-300">|</span>
            <span className="text-[0.6875rem] text-neutral-500">
              {isLoading ? 'se încarcă…' : 'actualizat acum câteva secunde'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[0.6875rem] text-neutral-500">Comparare:</span>
            <div className="inline-flex p-[2px] bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-[7px]">
              {([
                { v: 'none', label: 'Oprit' },
                { v: 'previous', label: 'Anterioară' },
                { v: 'yoy', label: 'Anul trecut' },
                { v: 'custom', label: 'Custom' },
              ] as { v: CompareMode; label: string }[]).map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setCompareMode(opt.v)}
                  className={
                    'text-[0.6875rem] font-medium px-2.5 py-1 border-0 rounded-[5px] cursor-pointer transition ' +
                    (prefs.compareMode === opt.v
                      ? 'bg-primary text-white'
                      : 'bg-transparent text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        </div>
            </div>
          <div
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg) translate3d(0, 0, 0.01px)',
              WebkitTransform: 'rotateY(180deg) translate3d(0, 0, 0.01px)',
              visibility: prefs.isFlipped ? 'visible' : 'hidden',
              transitionProperty: 'visibility',
              transitionDelay: prefs.isFlipped ? '0s' : '0.36s',
              background: '#FFFFFF',
              borderRadius: 'inherit',
              overflow: 'hidden',
            }}
          >
            <div key="back" className="h-full">
              <MonitorBackView
                metrics={metrics}
                activeMetrics={prefs.metrics}
                rangeLabel={rangeLabel}
                compareLabel={compareLabel}
              />
            </div>
          </div>
          </div>
        </div>

        <MetricPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onAdd={toggleMetric}
          activeMetrics={prefs.metrics}
        />

        {rangeModalOpen && (
          <DateRangeModal
            title="Perioadă personalizată"
            initialFrom={prefs.customFrom || isoDate(addDays(startOfDay(new Date()), -29))}
            initialTo={prefs.customTo || isoDate(startOfDay(new Date()))}
            onClose={() => setRangeModalOpen(false)}
            onApply={applyCustomRange}
          />
        )}

        {compareModalOpen && (
          <DateRangeModal
            title="Perioadă comparație"
            initialFrom={prefs.compareFrom || isoDate(addDays(primaryRange.from, -primaryDays))}
            initialTo={prefs.compareTo || isoDate(addDays(primaryRange.from, -1))}
            onClose={() => setCompareModalOpen(false)}
            onApply={applyCustomCompare}
          />
        )}

        {detailMetric && (
          <MetricDetailModal
            def={METRICS[detailMetric]}
            data={metrics[detailMetric]}
            primaryRange={primaryRange}
            onClose={() => setDetailMetric(null)}
          />
        )}
      </article>
    </div>
  );
}

// ───────── DATE RANGE MODAL ─────────
function DateRangeModal({
  title,
  initialFrom,
  initialTo,
  onClose,
  onApply,
}: {
  title: string;
  initialFrom: string;
  initialTo: string;
  onClose: () => void;
  onApply: (from: string, to: string) => void;
}) {
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);

  const valid = from && to && new Date(from) <= new Date(to);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-[6px] p-10"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-neutral-900 rounded-2xl max-w-[420px] w-full overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.25)]"
      >
        <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3 className="m-0 font-semibold tracking-[-0.01em] text-[1.125rem]">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 grid place-items-center hover:bg-neutral-200 dark:hover:bg-neutral-700 transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3.5">
          <div>
            <label className="block text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-neutral-500 mb-1.5">
              De la
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full h-10 px-3 text-[0.8125rem] border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div>
            <label className="block text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-neutral-500 mb-1.5">
              Până la
            </label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full h-10 px-3 text-[0.8125rem] border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/40">
          <button
            onClick={onClose}
            className="px-3.5 py-2 text-[0.7813rem] font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            Anulează
          </button>
          <button
            onClick={() => valid && onApply(from, to)}
            disabled={!valid}
            className="px-3.5 py-2 text-[0.7813rem] font-semibold text-white bg-primary border border-primary rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Aplică
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────── METRIC DETAIL MODAL ─────────
const METRIC_DETAIL_LINKS: Partial<Record<MetricId, { href: (range: DateRange) => string; label: string }>> = {
  sales:        { label: 'Vezi facturile', href: (r) => `/facturi?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
  proforma:     { label: 'Vezi proformele', href: (r) => `/proforme?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
  aviz:         { label: 'Vezi avizele', href: (r) => `/avize?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
  expenses:     { label: 'Vezi cheltuielile', href: (r) => `/cheltuieli?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
  payments:     { label: 'Vezi încasările', href: (r) => `/incasari?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
  debitors:     { label: 'Vezi facturile restante', href: () => `/facturi?status=overdue` },
  suppliers:    { label: 'Vezi plățile către furnizori', href: () => `/plati-furnizori` },
  stock:        { label: 'Vezi stocurile', href: () => `/stocuri` },
  newClients:   { label: 'Vezi clienții', href: (r) => `/clienti?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
  invoiceCount: { label: 'Vezi facturile', href: (r) => `/facturi?dateFrom=${isoDate(r.from)}&dateTo=${isoDate(r.to)}` },
};

function MetricDetailModal({
  def,
  data,
  primaryRange,
  onClose,
}: {
  def: MetricDefinition;
  data?: MetricData;
  primaryRange: DateRange;
  onClose: () => void;
}) {
  const total = data?.current || 0;
  const previous = data?.previous || 0;
  const delta = data?.deltaPct || 0;
  const series = data?.series || [];
  const fmt = def.unit === 'money' ? (n: number) => fmtMoney(n, false) : fmtCount;
  const unit = def.unit === 'money' ? 'RON' : (def.id === 'newClients' ? 'clienți' : 'facturi');
  const link = METRIC_DETAIL_LINKS[def.id];

  const deltaClass = Math.abs(delta) < 1 ? 'flat' : (delta > 0 ? 'up' : 'down');
  const deltaText = Math.abs(delta) < 1 ? 'stabil' : ((delta > 0 ? '+' : '') + delta.toFixed(1) + '%');

  // Sort series desc by value to show biggest contributors first; keep "all"
  const sortedSeries = [...series].sort((a, b) => b.v - a.v);
  const maxV = series.length > 0 ? Math.max(...series.map(s => s.v)) : 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full" style={{ background: def.color }} />
            <div>
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{def.label}</h2>
              <p className="text-[0.6875rem] text-neutral-500 dark:text-neutral-400 mt-0.5">
                {def.description} · {isoDate(primaryRange.from)} → {isoDate(primaryRange.to)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
              <div className="text-[0.6563rem] uppercase font-semibold tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Perioadă curentă</div>
              <div className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
                {fmt(total)} <span className="text-[0.6875rem] font-normal text-neutral-500 align-super">{unit}</span>
              </div>
            </div>
            <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
              <div className="text-[0.6563rem] uppercase font-semibold tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Perioadă anterioară</div>
              <div className="text-xl font-semibold text-neutral-700 dark:text-neutral-300 tabular-nums">
                {fmt(previous)} <span className="text-[0.6875rem] font-normal text-neutral-500 align-super">{unit}</span>
              </div>
            </div>
            <div className={
              'rounded-lg border p-3 ' +
              (deltaClass === 'up' ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800'
                : deltaClass === 'down' ? 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800'
                : 'border-neutral-200 dark:border-neutral-700')
            }>
              <div className="text-[0.6563rem] uppercase font-semibold tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Evoluție</div>
              <div className={
                'text-xl font-medium tabular-nums ' +
                (deltaClass === 'up' ? 'text-emerald-700 dark:text-emerald-400'
                  : deltaClass === 'down' ? 'text-red-700 dark:text-red-400'
                  : 'text-neutral-700 dark:text-neutral-300')
              }>
                {deltaText}
              </div>
            </div>
          </div>

          {/* Series breakdown */}
          {def.isTimeSeries && series.length > 0 && (
            <div>
              <h3 className="text-[0.6875rem] uppercase font-semibold tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">Defalcare pe perioadă</h3>
              <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                <div className="max-h-[300px] overflow-y-auto">
                  {sortedSeries.map((p, i) => {
                    const widthPct = (p.v / maxV) * 100;
                    return (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
                        <span className="text-[0.75rem] text-neutral-600 dark:text-neutral-400 w-24 shrink-0">{p.label}</span>
                        <div className="flex-1 h-4 bg-neutral-100 dark:bg-neutral-800 rounded relative overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 rounded transition-all"
                            style={{ width: `${widthPct}%`, background: def.color, opacity: 0.6 }}
                          />
                        </div>
                        <span className="text-[0.7813rem] font-medium tabular-nums text-neutral-800 dark:text-neutral-200 w-32 text-right shrink-0">
                          {fmt(p.v)} <span className="text-[0.625rem] text-neutral-500">{unit}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!def.isTimeSeries && (
            <p className="text-[0.7813rem] text-neutral-500 dark:text-neutral-400 italic">
              Acest indicator este un snapshot la momentul actual — nu are evoluție pe perioadă.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-neutral-100 dark:border-neutral-800 px-6 py-4 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 text-[0.7813rem] font-medium text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
          >
            Închide
          </button>
          {link && (
            <Link
              href={link.href(primaryRange)}
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[0.7813rem] font-semibold text-white bg-primary border border-primary rounded-lg hover:bg-primary-700 transition-colors"
            >
              {link.label}
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
